import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Button,
  Popover,
  MenuItem,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useModelCatalog, groupByProvider, formatPrice } from '../../../../../config/modelCatalog';
import useIsMobile from '../../../useIsMobile';
import '../../AssistantSettingsTheme.css';

const HOVER_DELAY_MS = 180;

/**
 * Extracts the display provider and bare model name from a stored model string.
 * Handles the legacy "azure:" prefix and unprefixed models as OpenAI.
 */
const getDisplayInfo = (modelValue) => {
  if (!modelValue)                          return { provider: 'OpenAI',    label: '' };
  if (modelValue.startsWith('anthropic:'))  return { provider: 'Anthropic', label: modelValue.replace('anthropic:', '') };
  if (modelValue.startsWith('google-gla:')) return { provider: 'Google',    label: modelValue.replace('google-gla:', '') };
  if (modelValue.startsWith('openai:'))     return { provider: 'OpenAI',    label: modelValue.replace('openai:', '') };
  if (modelValue.startsWith('azure:'))      return { provider: 'OpenAI',    label: modelValue.replace('azure:', '') };
  return { provider: 'OpenAI', label: modelValue };
};

const LlmConfigSettings = ({ values, onChange, channel = 'web' }) => {
  const llmConfig = values.llm_config || {};
  const currentModel = llmConfig.model || '';
  const { provider: currentProvider, label: currentLabel } = getDisplayInfo(currentModel);

  const { models } = useModelCatalog(channel);
  const MODEL_OPTIONS = useMemo(() => groupByProvider(models), [models]);
  const PROVIDERS = useMemo(() => Object.keys(MODEL_OPTIONS), [MODEL_OPTIONS]);
  const isMobile = useIsMobile();

  const [anchorEl, setAnchorEl]           = useState(null);
  const [activeProvider, setActiveProvider] = useState(currentProvider);
  const hoverTimer = useRef(null);

  const handleOpen = (e) => {
    setActiveProvider(currentProvider);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    clearTimeout(hoverTimer.current);
    setAnchorEl(null);
  };

  /** Debounced hover, prevents accidental switches while moving to the right panel. */
  const handleProviderHover = useCallback((provider) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setActiveProvider(provider), HOVER_DELAY_MS);
  }, []);

  const handleProviderLeave = useCallback(() => {
    clearTimeout(hoverTimer.current);
  }, []);

  const handleSelectModel = (modelValue) => {
    onChange('llm_config.model', modelValue);
    handleClose();
  };

  return (
    <Box className="assistantSettingsPanel" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="subtitle1">LLM Configuration</Typography>

      <FormControl fullWidth>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          LLM Model
        </Typography>
        <Button
          variant="outlined"
          onClick={handleOpen}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            color: 'text.primary',
            borderColor: 'rgba(0,0,0,0.23)',
            borderRadius: 2.5,
            height: 56,
            px: 1.75,
            '&:hover': { borderColor: 'text.primary' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={currentProvider}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1.5 }}
            />
            <Typography variant="body1">
              {currentLabel || 'Select a model'}
            </Typography>
          </Box>
        </Button>
      </FormControl>

      {/* Two-panel cascading picker ---------------------------------------- */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              // The 460px two-panel layout does not fit a phone, so mobile stacks
              // the provider list above the model list and fills the viewport.
              flexDirection: isMobile ? 'column' : 'row',
              minWidth: isMobile ? 0 : 460,
              width: isMobile ? 'calc(100vw - 32px)' : undefined,
              maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined,
              borderRadius: 3,
              mt: 0.5,
            },
          },
        }}
      >
        {/* Provider list, hover switching is pointer-only, so mobile relies on tap */}
        <Box
          sx={{
            width: isMobile ? '100%' : 150,
            display: isMobile ? 'flex' : 'block',
            overflowX: isMobile ? 'auto' : undefined,
            borderRight: isMobile ? 0 : 1,
            borderBottom: isMobile ? 1 : 0,
            borderColor: 'divider',
            py: 1,
            flexShrink: 0,
          }}
          onMouseLeave={handleProviderLeave}
        >
          {PROVIDERS.map((provider) => (
            <MenuItem
              key={provider}
              selected={provider === activeProvider}
              onMouseEnter={isMobile ? undefined : () => handleProviderHover(provider)}
              onClick={() => {
                clearTimeout(hoverTimer.current);
                setActiveProvider(provider);
              }}
              sx={{
                justifyContent: 'space-between',
                py: 1.5,
                mx: 0.5,
                borderRadius: 1.5,
                flexShrink: isMobile ? 0 : undefined,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={provider === activeProvider ? 600 : 400}
              >
                {provider}
              </Typography>
              {!isMobile && <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            </MenuItem>
          ))}
        </Box>

        {/* Model list for the active provider */}
        <Box sx={{ flex: 1, py: 1, overflowY: 'auto', maxHeight: isMobile ? '50vh' : 320 }}>
          {MODEL_OPTIONS[activeProvider]?.map((model) => (
            <MenuItem
              key={model.value}
              selected={model.value === currentModel}
              onClick={() => handleSelectModel(model.value)}
              sx={{
                justifyContent: 'space-between',
                mx: 0.5,
                borderRadius: 1.5,
              }}
            >
              <Typography variant="body2">{model.label}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2, flexShrink: 0 }}>
                {formatPrice(model)}
              </Typography>
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default LlmConfigSettings;
